import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { MutliDropdown } from "../shared";
import {
  GENERAL_ERROR_MESSAGE,
  SWITCH_CUSTOMER_MESSAGES,
} from "../../utils/message";
import { useMessage } from "../../context/MessageContext";
import { postApi } from "../../apis/api";
import {
  GET_CUSTOMER_LIST,
  SWITCH_PARENT_CUSTOMER,
} from "../../apis/end-points";
import { Loader } from "../loaders/loaders";
import { AddedByTenantsTypes } from "../navbar/Navbar";
import { useDispatch } from "react-redux";
import {
  loginUser,
  setCustomerLevel,
  setDirectCustomer,
  setIsCUSTOMER,
  setIsUserOfOmniMove,
} from "../../redux/reducer/auth/authSlice";
import {
  setUserInfo,
  setComanyLogo,
} from "../../redux/reducer/navbar/navbarSlice";

import { SingleSettingIcon, XCrossIcon } from "../../assets";

interface SwitchUserModalProps {
  handleClose: () => void;
  switchCustomerData?: AddedByTenantsTypes[];
  setSwitchCustomerData?: React.Dispatch<
    React.SetStateAction<AddedByTenantsTypes[]>
  >;
  customerSelectedData: AddedByTenantsTypes;
  setCustomerSelectedData: React.Dispatch<
    React.SetStateAction<AddedByTenantsTypes>
  >;
}

interface ColumnTypes {
  key: string;
  title: string;
}

const switchCustomerColumns: ColumnTypes[] = [
  { key: "customerName", title: "Customer Name" },
  { key: "yourAccount", title: "Your Account" },
  { key: "userType", title: "User Type" },
  { key: "customerNumber", title: "Customer Number" },
];

const SwitchUserModal: React.FC<SwitchUserModalProps> = ({
  handleClose,
  // switchCustomerData,
  // setSwitchCustomerData,
  customerSelectedData,
  setCustomerSelectedData,
}) => {
  const { setMessage } = useMessage();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [switchCustomerData, setSwitchCustomerData] = useState<
    AddedByTenantsTypes[]
  >([]);
  const userEmail = localStorage?.getItem("email");
  const companyId = localStorage?.getItem("companyId");
  const [hasManualSelection, setHasManualSelection] = useState<boolean>(false);
  // Memoize the disabling logic for the save button
  // const isButtonDisabled = useMemo(() => {
  //   const areFieldsComplete = customerSelectedData?.id !== "";
  //   return !areFieldsComplete;
  // }, [customerSelectedData]);
  // Memoize the disabling logic for the save button
  const isButtonDisabled = useMemo(() => {
    // Button should be disabled if no manual selection has been made or no option is selected
    return (
      !hasManualSelection ||
      !customerSelectedData?.id ||
      customerSelectedData?.id === ""
    );
  }, [customerSelectedData, hasManualSelection]);

  // Wrapper function to handle manual selection from dropdown
  const handleManualSelection = (selectedData: AddedByTenantsTypes) => {
    setCustomerSelectedData(selectedData);
    setHasManualSelection(true);
  };

  // Preselect customer by parentCustomerId when options load
  useEffect(() => {
    if (!companyId || companyId === "null") return;
    if (customerSelectedData?.id) return;
    const match = switchCustomerData?.find(
      (item) => String(item?.id) === String(companyId)
    );

    if (match && customerSelectedData?.id !== match.id) {
      setCustomerSelectedData(match);
      // Explicitly keep hasManualSelection as false for auto-selection
      setHasManualSelection(false);
    }
  }, [
    companyId,
    switchCustomerData,
    customerSelectedData?.id,
    setCustomerSelectedData,
  ]);

  /**
   * Handles the customer switch action by sending the selected customer ID
   * to the API endpoint and managing the loading state and error messages.
   */
  const handleChangeCustomer = async () => {
    if (isButtonDisabled) {
      return;
    }
    setLoading(true);
    try {
      const payload = {
        parentCustomerId: customerSelectedData.id,
      };
      const apiResponse = await postApi<any>(SWITCH_PARENT_CUSTOMER, payload);

      if (apiResponse.success) {
        const data: any = apiResponse.data ?? [];
        const isCustomer = data.role.name === "admin" && data?.isCustomerAdmin;
        const isUserOfOmniMove = data?.isUserOfOmniMove || false;
        const customer = data.customer;
        const agent = data.agent;

        await dispatch(
          loginUser({
            userRole: data?.role?.name || "",
            token: data?.access_token || null,
            email: data?.email,
            expiration: Date.now() / 1000,
            customer: customer,
            parentCustomerId: data?.parentCustomerId,
            agent: agent,
          })
        );
        await dispatch(
          setUserInfo({
            username: `${data?.firstName} ${data?.lastName}`,
            userId: data?.id || "",
            userEmail: data?.email || "",
            userRole: data?.role?.name || "",
            userProfile: `${data?.firstName?.[0] || ""}${data?.lastName?.[0] || ""
              }`,
          })
        );
        await dispatch(
          setDirectCustomer({
            isDirectCustomer: data?.isDirectCustomer ?? false,
            isUserOfDirectCustomer: data?.isUserOfDirectCustomer ?? false,
          })
        );
        await dispatch(setCustomerLevel(data?.customerLevel));
        await dispatch(setIsCUSTOMER(isCustomer));
        await dispatch(setIsUserOfOmniMove(isUserOfOmniMove));
        await dispatch(setComanyLogo(customer?.logo));
        // Redirect to external URL in the same tab - session is preserved in localStorage
        if (data?.customerLevel !== "zero") {
          localStorage.clear();
          sessionStorage.clear();
          const URL = `https://${data?.redirectUrl}.uat.dtslastmile.com/?sessionId=${data?.sessionId}`;
          window.location.href = URL;
        } else if (data?.customerLevel === "zero") {
          localStorage.clear();
          sessionStorage.clear();
          const URL = `https://uat.dtslastmile.com/?sessionId=${data?.sessionId}`;
          window.location.href = URL;
        } else {
          handleClose();
          setMessage(SWITCH_CUSTOMER_MESSAGES.SUCCESS_CHANGE, "success");
          navigate("/");
        }
      } else {
        setMessage(
          apiResponse.error?.message ?? GENERAL_ERROR_MESSAGE,
          "error"
        );
      }
    } catch (error) {
      setMessage(GENERAL_ERROR_MESSAGE, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <Loader />}
      <div className="modal modal--hazmatDetail modal--open">
        <div className="modal__dialog">
          <div className="modal__content modal__content--lg780">
            <div className="modal__header">
              <h4 className="modal__header--title">Select Account</h4>
              <button
                className="btn btn__transparent btnClose"
                onClick={handleClose}
              >
                <XCrossIcon />
              </button>
            </div>
            <div className="modal__body">
              {/* <div className="formRow">
                <div className="formCol">
                  <h3 className="currentUser">
                    Current User: <span>Customer</span>
                  </h3>
                </div>
              </div> */}
              <div className="formRow">
                <MutliDropdown
                  columns={switchCustomerColumns}
                  data={switchCustomerData}
                  setData={setSwitchCustomerData}
                  captionTitles="-Select-"
                  rowData={customerSelectedData}
                  setRowData={handleManualSelection}
                  // setRowData={setCustomerSelectedData}
                  displayColumn="customerName"
                  // apiUrl={""}
                  // apiUrl="/user/auth/parent-customer"
                  apiUrl={GET_CUSTOMER_LIST}
                  type={"dropdown"}
                  labelTitle="Account Name"
                  extendedUrl={{
                    emailId: userEmail || "",
                  }}
                // labelTitle="Panel User"
                />
              </div>
            </div>
            <div className="modal__footer">
              <div className="btn__group">
                <button className="btn btn__white" onClick={handleClose}>
                  Cancel
                </button>
                <button
                  className="btn btn__primary"
                  disabled={isButtonDisabled}
                  onClick={handleChangeCustomer}
                >
                  Go
                </button>
                <button
                  className="backBtn"
                  // disabled={isButtonDisabled}
                  onClick={() => {
                    navigate("/user/profile", {
                      state: { from: "switchUserModal" },
                    });
                    handleClose();
                  }}
                >
                  <SingleSettingIcon />
                  Go to Settings
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="modal__backdrop"></div>
      </div>
    </>
  );
};

export default SwitchUserModal;
